import type { ResearchDelivery, ResearchStatus } from './delivery';

export interface DefaultNoticeCopy {
  text: string;
  textReplay: string;
  stop: string;
  dismiss: string;
}

interface NoticeStore { getItem(key: string): string | null; setItem(key: string, value: string): void }

/**
 * One seen marker per scope: the plain notice, and the replay one. Replay switched on by default later
 * is news the learner has not been told, so dismissing the plain notice does not cover it.
 */
const seenKey = (subject: string, replay: boolean): string => `li-research-default-notice:${subject}${replay ? ':replay' : ''}`;
const namesReplay = (status: ResearchStatus): boolean => Boolean(status.replay?.enabled && status.replay.defaultOn);

/** The notice is for an active owner-default account that has not yet dismissed this scope's wording on this device. */
export function shouldShowDefaultNotice(status: ResearchStatus, store: NoticeStore): boolean {
  if (!status.active || status.consentSource !== 'owner-default' || !status.subjectId) return false;
  try { return store.getItem(seenKey(status.subjectId, namesReplay(status))) !== '1'; } catch { return true; }
}

/** OK on the notice: remember the wording that was shown (the replay wording covers the plain one too). */
export function markDefaultNoticeSeen(status: ResearchStatus, store: NoticeStore): void {
  if (!status.subjectId) return;
  try {
    store.setItem(seenKey(status.subjectId, false), '1');
    if (namesReplay(status)) store.setItem(seenKey(status.subjectId, true), '1');
  } catch { /* shown again next time */ }
}

/**
 * A visible notice shown until dismissed once per device and scope. It sits at the top and grows --safe-top
 * by its own height while shown, so every screen's top-anchored layout moves down instead of being covered.
 * Stop is the same stop-and-clear the settings control offers. The replay adapter listens for research going
 * inactive and ends capture in this tab; the server withdraws replay with the semantic opt-out.
 */
export function mountDefaultNotice(delivery: ResearchDelivery, copyNow: () => DefaultNoticeCopy): () => void {
  const bar = document.createElement('div');
  bar.className = 'research-default-notice';
  bar.hidden = true;
  // Only the sentence is a live region: a status role around the buttons would hide them from some readers.
  const text = document.createElement('span');
  text.setAttribute('role', 'status');
  const stop = document.createElement('button');
  stop.type = 'button';
  stop.className = 'research-default-stop';
  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'research-default-ok';
  bar.append(text, stop, dismiss);
  const root = document.documentElement;
  const reserve = () => {
    if (bar.hidden) root.style.removeProperty('--safe-top');
    else root.style.setProperty('--safe-top', `calc(env(safe-area-inset-top, 0px) + ${Math.ceil(bar.getBoundingClientRect().height)}px)`);
  };
  const sizer = typeof ResizeObserver === 'function' ? new ResizeObserver(reserve) : null;
  sizer?.observe(bar);
  const store = (): NoticeStore => { try { return localStorage; } catch { return { getItem: () => null, setItem: () => {} }; } };
  const render = () => {
    const status = delivery.status;
    bar.hidden = !shouldShowDefaultNotice(status, store());
    const copy = copyNow();
    // Which wording applies, set once the status names a participant (a browser check can wait on it, hidden or not).
    if (status.subjectId) bar.setAttribute('data-scope', namesReplay(status) ? 'replay' : 'plain');
    else bar.removeAttribute('data-scope');
    text.textContent = namesReplay(status) ? copy.textReplay : copy.text;
    stop.textContent = copy.stop;
    dismiss.textContent = copy.dismiss;
    reserve();
  };
  const onDismiss = () => { markDefaultNoticeSeen(delivery.status, store()); bar.hidden = true; reserve(); };
  const onStop = async () => {
    stop.disabled = true;
    const ok = await delivery.optOut();
    stop.disabled = false;
    if (ok) onDismiss(); else render();
  };
  // OK in another tab writes the seen marker there; this tab hides its notice too.
  const onStorage = (event: StorageEvent) => { if (event.key?.startsWith('li-research-default-notice:')) render(); };
  dismiss.addEventListener('click', onDismiss);
  stop.addEventListener('click', onStop);
  window.addEventListener('storage', onStorage);
  window.addEventListener('li-research-state', render);
  window.addEventListener('li-language', render);
  document.body.prepend(bar);
  render();
  return () => {
    dismiss.removeEventListener('click', onDismiss);
    stop.removeEventListener('click', onStop);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('li-research-state', render);
    window.removeEventListener('li-language', render);
    sizer?.disconnect();
    bar.remove();
    root.style.removeProperty('--safe-top');
  };
}
